import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import PropTypes from 'prop-types';
import encryptData from "../config/encryptConfig";

const ResetPasswordModal = ({ isOpen, onClose }) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const safeT = (key, fallback) => {
    const translated = t(key);
    if (!translated || translated === key) return fallback;
    return translated;
  };

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setError('');
    setMessage('');
    if (!value?.trim()) {
      setError(safeT('login.enter_email', 'Please enter an email'));
      return;
    }

    setLoading(true);
    try {
      // Use apiService.post to keep consistent headers and base URL
      const endpoint = '/auth/forgot-password';
     const payload = {
  email: encryptData(value.trim()),
  username: encryptData(value.trim())
};


      // apiService.post will call makeRequest which throws on non-2xx
      const resp = await apiService.post(endpoint, payload);


      // Some backends return message in resp.message
      const successMsg = resp?.message || safeT('login.reset_link_sent', 'Reset link sent to your gmail');
      setMessage(successMsg);
      setError('');
      setLoading(false);
      // auto-close after a short delay so the user sees the success message
      setTimeout(() => {
        setValue('');
        if (typeof onClose === 'function') onClose();
      }, 1600);
    } catch (err) {
      console.error('Forgot-password error:', err);
      const raw = (err && (err.body?.message || err.message || '')).toString().toLowerCase();
      const isNotFound = err?.status === 404 || raw.includes('not found') || raw.includes('user not found') || raw.includes('no user') || raw.includes('404');
      if (isNotFound) {
        setError(safeT('login.user_not_found', 'User not found '));
      } else {
        setError(err.message || safeT('login.reset_failed', 'Failed to send reset link'));
      }
      setMessage('');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50">
      <div className="bg-white rounded-2xl shadow-xl w-96 p-6">
        {/* Header */}
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t('login.forgotPassword')}
        </h2>

        {/* Input */}
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t('login.forgotnote')}
        </label>
        <input
          type="email"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={t('login.placeholderresetpassword')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={loading}
        />

        {error && (
          <div className="text-sm text-red-700 mb-2">{error}</div>
        )}
        {message && (
          <div className="text-sm text-green-700 mb-2">{message}</div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
            onClick={onClose}
            disabled={loading}
          >
           {t('login.cancel')}
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition flex items-center gap-2"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
            ) : null}
            <span>{t('login reset') || 'Send Reset Link'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;

ResetPasswordModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};
